import {
  IonApp,
  IonRouterOutlet,

} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import HomeContent from "../pages/Home/HomeContent";
import UnsubscribePage from "../pages/Unsubscribe/UnsubscribePage";
import QuestionnairePreview from "../pages/QuestionnairePreview/QuestionnairePreview";
import ContentPreview from "../pages/ContentPreview/ContentPreview";

import { useAuth } from "../contexts/AuthContext";
import { useConfig } from "../contexts/ConfigContext";
import Tabs from "./Tabs";
import PrivateRoute from "./PrivateRoute";
import AppUrlListener from "../AppUrlListener";
import { getStudySlug, isSingleStudy } from "../helper/study";
import { getDefaultTabPath } from "./studyTabs";



const Navigator: React.FC = () => {
  // For loading state
  const { isInitialized, isAuthenticated, signupInProgress } = useAuth();
  // Role-aware landing: investigators land on the suivi tab, everyone else on the
  // first role-free tab. Safe pre-resolution: role=undefined hides gated tabs.
  const { selectedRole } = useConfig();

  const single = isSingleStudy();
  const slug = getStudySlug();

  return (
    <IonApp>
      <IonReactRouter>
        <AppUrlListener />
        <IonRouterOutlet>
          {single ? (
            // Single-study: slug-less landing at "/". Subjects (and, next step, the role
            // links) live at the root; logged-in users go to the slug-prefixed tabs.
            <Route
              exact
              path="/"
              render={() =>
                !isInitialized ? null : isAuthenticated && !signupInProgress ? (
                  <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />
                ) : (
                  <HomeContent />
                )
              }
            />
          ) : (
            // Multi-study: no default study at "/", keep the previous redirect behavior.
            <PrivateRoute
              exact
              path="/"
              render={() => <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />}
            />
          )}

          {/* Single-segment entry (e.g. /mentalplus, /investigator, /parent — also the
              push-notification target). Single mode: the segment selects the role; the study
              slug and unknown segments fall back to the subject landing inside HomeContent.
              Multi mode: the segment is the study slug → redirect to its slug-prefixed welcome.
              Explicit route because IonRouterOutlet doesn't render a pathless fallback. */}
          <Route
            exact
            path="/:entry"
            render={({ match }) =>
              !isInitialized ? null : single ? (
                isAuthenticated && !signupInProgress ? (
                  <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />
                ) : (
                  <HomeContent entrySegment={match.params.entry} />
                )
              ) : (
                <Redirect to={`/${match.params.entry}/welcome`} />
              )
            }
          />

          {/* Public landing (slug-prefixed): the multi-study landing route, and the
              slug-prefixed entry that in-app logout/leave redirects and the NoSlug page use.
              Auth-guarded in single mode (like "/" and "/:study_slug") so an authenticated
              user landing here is sent to the app instead of being logged out by
              HomeContent's onboarding effect. */}
          <Route
            exact
            path="/:study_slug/welcome"
            render={() =>
              !isInitialized ? null : single && isAuthenticated && !signupInProgress ? (
                <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />
              ) : (
                <HomeContent />
              )
            }
          />

          {/* Authenticated app (slug kept in the URL). */}
          <Route path="/:study_slug/tabs" render={() => <Tabs />} />

          {/* Multi-study role entry: "/{study}/role/{roleSlug}" (e.g. /mentalplus/role/psychologue).
              The literal "/role/" infix keeps it from colliding with "/:study_slug/welcome" and
              "/:study_slug/tabs". The study slug is read from the first segment (getStudySlug),
              the role from :entry — HomeContent pre-selects the role whose url_slug matches it.
              Single-study role entries use the slug-less "/:entry" route above instead. */}
          <Route
            exact
            path="/:study_slug/role/:entry"
            render={({ match }) =>
              !isInitialized ? null : isAuthenticated && !signupInProgress ? (
                <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />
              ) : (
                <HomeContent entrySegment={match.params.entry} />
              )
            }
          />

          {/* Public no-login questionnaire flow preview ("Simulate" button in Diapason). The
              signed ?token= authorises the read, so no auth/account is needed. The literal
              "/preview/" infix (3 segments) can't collide with "/:study_slug/welcome",
              "/:study_slug/tabs" or "/:study_slug/role/:entry", and a plain <Route> (not
              PrivateRoute) keeps it from being bounced to login. */}
          <Route
            exact
            path="/:study_slug/preview/:questionnaireId"
            render={() => <QuestionnairePreview />}
          />

          {/* Public no-login content page preview ("Preview" button on Diapason content
              pages) — same reasoning as the questionnaire preview route above: literal
              multi-segment infix, signed ?token=, plain <Route>. */}
          <Route
            exact
            path="/:study_slug/content-preview/:contentId"
            render={() => <ContentPreview />}
          />

          {/* Public no-login unsubscribe / notification-preference page reached from the
              email link. The signed ?token= identifies the user, so no study slug or auth is
              needed. IMPORTANT: it MUST come AFTER "/:entry" — IonRouterOutlet's matchRoute
              keeps the LAST matching child (not the first, unlike <Switch>), so a single
              segment like "/unsubscribe" is otherwise swallowed by "/:entry" → welcome page.
              The multi-segment routes above never match a one-segment path, so this stays the
              last (winning) match. */}
          <Route exact path="/unsubscribe" render={() => <UnsubscribePage />} />

          {/* Fallback */}
          <Route>
            {isAuthenticated ? (
              <Redirect to={getDefaultTabPath(slug, selectedRole?.name)} />
            ) : single ? (
              <Redirect to="/" />
            ) : (
              <Redirect to={`/${slug}/welcome`} />
            )}
          </Route>
        </IonRouterOutlet>
      </IonReactRouter>
    </IonApp>
  );
};

export default Navigator;
